import { AxiosInstance, AxiosResponse } from "axios";
import * as XLSX from "xlsx";

interface CustomerFilters {
  startDate?: string;
  endDate?: string;
  search?: string;
  category?: boolean;
  city?: boolean;
  state?: boolean;
}

interface ProductFilters {
  startDate?: string;
  endDate?: string;
  date?: boolean;
  search?: string;
  name?: boolean;
}

interface ReportFilters {
  startDate?: string;
  endDate?: string;
  dateOfVisit?: boolean;
  customer?: boolean;
  customerCategory?: boolean;
  followUpPlan?: boolean;
  customerName?: string;
}

interface CustomerExportData {
  id: number;
  name: string;
  state: string;
  city: string;
  phone: string;
  email: string;
  category: string;
  nature: string;
  pop: string;
  planned: number;
  achieved: number;
  specialization: string;
  salesRep?: string;
  target: number;
  call_rate: number;
  created_at: string;
}

interface ProductExportData {
  id: number;
  name: string;
  plan: number;
  achieved: number | null;
  pending: number | null;
  most_used_content: string | null;
  least_used_content: string | null;
  companyName?: string;
  date_created: string;
  date_updated: string;
}

interface ReportExportData {
  id: number;
  objective: string;
  dateOfVisit: string;
  content: string[] | string;
  product: string[] | string;
  customer: string;
  customerCategory: "A" | "B" | "C";
  comment: string;
  followUpPlan: string | null;
  summary: string | null;
  salesRep: string;
  manager: string | null;
  created_at: string;
  timeOfVisit: string;
}

interface ExportPagination {
  totalPages?: number;
  totalCustomers?: number;
  totalProducts?: number;
  totalRecords?: number;
  limit?: number;
}

interface PaginatedExportResponse<T> {
  data: T[] | { reports: T[]; pagination?: ExportPagination };
  pagination?: ExportPagination;
  message?: string;
  success?: boolean;
  status?: string;
}

const normalizeExportPage = <T,>(page: PaginatedExportResponse<T>) => {
  if (page.success === false || page.status === "error") {
    throw new Error(page.message || "Export failed");
  }
  if (Array.isArray(page.data)) {
    return { data: page.data, pagination: page.pagination };
  }
  if (page.data && Array.isArray(page.data.reports)) {
    return { data: page.data.reports, pagination: page.data.pagination };
  }
  throw new Error("Invalid export response");
};

const EXPORT_PAGE_SIZE = 100;

const formatDateTime = (value?: string) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const formatCallRate = (rate?: number) => {
  const parsedRate = Number(rate ?? 0);
  if (Number.isNaN(parsedRate)) return "0%";
  return `${(parsedRate * 100).toFixed(2)}%`;
};

const sanitizeSheetName = (sheetName: string) =>
  sheetName.replace(/[\\/*?:[\]]/g, "").slice(0, 31) || "Export";

const buildUrl = (
  endpoint: string,
  page: number,
  limit: number,
  params: Record<string, string>
) => {
  const queryParams = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    ...params,
  });

  const separator = endpoint.includes("?") ? "&" : "?";
  return `${endpoint}${separator}${queryParams.toString()}`;
};

const ensureSuccessResponse = <T,>(response: AxiosResponse<T>) => {
  if (response.status >= 400) {
    const errorMessage =
      (response.data as { message?: string })?.message || "Export failed";
    throw new Error(errorMessage);
  }

  return response.data;
};

const fetchAllPages = async <T,>(
  client: AxiosInstance,
  endpoint: string,
  params: Record<string, string>,
  totalRecordsKey: "totalCustomers" | "totalProducts" | "totalRecords"
) => {
  const firstPageResponse = normalizeExportPage(
    ensureSuccessResponse(
      await client.get<PaginatedExportResponse<T>>(
        buildUrl(endpoint, 1, EXPORT_PAGE_SIZE, params)
      )
    )
  );

  const firstPageData = firstPageResponse.data || [];
  const totalPages =
    firstPageResponse.pagination?.totalPages ||
    Math.ceil(
      (firstPageResponse.pagination?.[totalRecordsKey] || firstPageData.length) /
        EXPORT_PAGE_SIZE
    ) ||
    1;

  if (totalPages <= 1) {
    return firstPageData;
  }

  const remainingPages = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, index) =>
      client.get<PaginatedExportResponse<T>>(
        buildUrl(endpoint, index + 2, EXPORT_PAGE_SIZE, params)
      )
    )
  );

  const remainingData = remainingPages.flatMap((response) => {
    const pageData = normalizeExportPage(ensureSuccessResponse(response));
    return pageData.data || [];
  });

  return [...firstPageData, ...remainingData];
};

const downloadRows = (
  rows: Record<string, string | number | null>[],
  fileName: string,
  sheetName: string
) => {
  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    workbook,
    worksheet,
    sanitizeSheetName(sheetName)
  );

  if (fileName.toLowerCase().endsWith(".csv")) {
    XLSX.writeFile(workbook, fileName, { bookType: "csv" });
    return;
  }

  XLSX.writeFile(workbook, fileName);
};

const buildCustomerParams = (filters: CustomerFilters) => {
  const params: Record<string, string> = {};

  if (filters.startDate) params.start_date = filters.startDate;
  if (filters.endDate) params.end_date = filters.endDate;
  if (filters.search) params.name = filters.search;
  if (filters.category !== undefined) {
    params.category = String(filters.category);
  }
  if (filters.city !== undefined) params.city = String(filters.city);
  if (filters.state !== undefined) params.state = String(filters.state);

  return params;
};

const buildProductParams = (filters: ProductFilters) => {
  const params: Record<string, string> = {};

  if (filters.startDate) params.start_date = filters.startDate;
  if (filters.endDate) params.end_date = filters.endDate;
  if (filters.search) params.name = filters.search;
  if (filters.date !== undefined) params.date = String(filters.date);
  if (filters.name !== undefined) params.contentName = String(filters.name);

  return params;
};

const buildReportParams = (filters: ReportFilters) => {
  const params: Record<string, string> = {};

  if (filters.startDate) params.startDate = filters.startDate;
  if (filters.endDate) params.endDate = filters.endDate;
  if (filters.customerName) params.customerName = filters.customerName;
  if (filters.dateOfVisit !== undefined) {
    params.dateOfVisit = String(filters.dateOfVisit);
  }
  if (filters.customer !== undefined) {
    params.customer = String(filters.customer);
  }
  if (filters.customerCategory !== undefined) {
    params.customerCategory = String(filters.customerCategory);
  }
  if (filters.followUpPlan !== undefined) {
    params.followUpPlan = String(filters.followUpPlan);
  }

  return params;
};

const parseArrayField = (value: string[] | string | null | undefined) => {
  if (!value) return "";

  if (Array.isArray(value)) {
    return value.join(", ");
  }

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.join(", ") : String(value);
  } catch (error) {
    return String(value);
  }
};

const mapReportToExportRow = (report: ReportExportData, index = 0) => ({
  "S/N": index + 1,
  Customer: report.customer || "",
  Category: report.customerCategory || "",
  "Sales Rep": report.salesRep || "",
  Manager: report.manager || "",
  Objective: report.objective || "",
  "Date of Visit": formatDateTime(report.dateOfVisit),
  "Time of Visit": report.timeOfVisit || "",
  Products: parseArrayField(report.product),
  Contents: parseArrayField(report.content),
  Summary: report.summary || "",
  Comment: report.comment || "",
  "Follow Up Plan": report.followUpPlan || "",
  "Created At": formatDateTime(report.created_at),
});

export const exportCustomersToExcel = async ({
  client,
  endpoint,
  filters,
  fileName,
  sheetName,
}: {
  client: AxiosInstance;
  endpoint: string;
  filters: CustomerFilters;
  fileName: string;
  sheetName: string;
}) => {
  const customers = await fetchAllPages<CustomerExportData>(
    client,
    endpoint,
    buildCustomerParams(filters),
    "totalCustomers"
  );

  downloadRows(
    customers.map((customer, index) => ({
      "S/N": index + 1,
      Name: customer.name || "",
      Category: customer.category || "",
      Specialization: customer.specialization || "",
      "Sales Rep": customer.salesRep || "",
      "Annual Target": customer.target ?? 0,
      "Planned Visits": customer.planned ?? 0,
      "Achieved Visits": customer.achieved ?? 0,
      "Call Rate": formatCallRate(customer.call_rate),
      Email: customer.email || "",
      Phone: customer.phone || "",
      State: customer.state || "",
      City: customer.city || "",
      "Place of Practice": customer.pop || "",
      "Nature of Business": customer.nature || "",
      "Date Created": formatDateTime(customer.created_at),
    })),
    fileName,
    sheetName
  );
};

export const exportReportsToExcel = async ({
  client,
  endpoint,
  filters,
  fileName,
  sheetName,
}: {
  client: AxiosInstance;
  endpoint: string;
  filters: ReportFilters;
  fileName: string;
  sheetName: string;
}) => {
  const reports = await fetchAllPages<ReportExportData>(
    client,
    endpoint,
    buildReportParams(filters),
    "totalRecords"
  );

  downloadRows(
    reports.map((report, index) => mapReportToExportRow(report, index)),
    fileName,
    sheetName
  );
};

export const exportSingleReportToExcel = ({
  report,
  fileName,
  sheetName,
}: {
  report: ReportExportData;
  fileName: string;
  sheetName: string;
}) => {
  downloadRows([mapReportToExportRow(report)], fileName, sheetName);
};

export const exportProductsToExcel = async ({
  client,
  endpoint,
  filters,
  fileName,
  sheetName,
}: {
  client: AxiosInstance;
  endpoint: string;
  filters: ProductFilters;
  fileName: string;
  sheetName: string;
}) => {
  const products = await fetchAllPages<ProductExportData>(
    client,
    endpoint,
    buildProductParams(filters),
    "totalProducts"
  );

  downloadRows(
    products.map((product, index) => ({
      "S/N": index + 1,
      Name: product.name || "",
      Plan: product.plan ?? 0,
      Achieved: product.achieved ?? 0,
      Pending: product.pending ?? 0,
      "Most Used Content": product.most_used_content || "",
      "Least Used Content": product.least_used_content || "",
      Company: product.companyName || "",
      "Date Created": formatDateTime(product.date_created),
      "Date Updated": formatDateTime(product.date_updated),
    })),
    fileName,
    sheetName
  );
};
